import {useCallback, useState, useEffect} from 'react';

import {useGridApiEventListener} from '../hooks/useGridApiEventListener';
import {ICellRendererParams, AgGridEvent} from '../types/AgGridTypes';
import {DataGridProps} from '../types/DataGridProps';
import {DefaultRowSelectCheckboxRenderer} from './DefaultRowSelectCheckboxRenderer';

export interface RowSelectHeaderRendererProps extends ICellRendererParams {
  gridProps: DataGridProps;
}

export function RowSelectHeaderRenderer({api, context}: RowSelectHeaderRendererProps) {
  const getSelectedStateFromNode = () => {
    const selectionArray: Array<boolean | undefined> = [];
    api.forEachNode((node) => {
      if (node.data) {
        selectionArray.push(node.isSelected());
      }
    });

    if (selectionArray.length === 0) {
      return false;
    }

    if (selectionArray.every((i) => i === true)) {
      return true;
    }
    if (selectionArray.every((i) => i === false)) {
      return false;
    }
    return undefined;
  };

  const [selected, setSelected] = useState<boolean | undefined>(getSelectedStateFromNode);

  const handleSelectionChanged = useCallback(
    (event: AgGridEvent) => {
      const newSelectedState = getSelectedStateFromNode();
      setSelected(newSelectedState);
    },
    [getSelectedStateFromNode]
  );

  const {addListeners, removeListeners} = useGridApiEventListener(
    api,
    ['selectionChanged'],
    handleSelectionChanged
  );

  useEffect(() => {
    addListeners();

    return () => {
      removeListeners();
    };
  }, [addListeners, removeListeners]);

  const change = useCallback(() => {
    const newlySelected = !selected;
    setSelected(newlySelected);
    if (newlySelected) {
      api.forEachNode((node) => {
        if (node.data) {
          node.setSelected(true);
        }
      });
    } else {
      api.deselectAll();
    }
    api.refreshCells({force: true, columns: ['eag-col-select']});
  }, [api, selected]);

  return (
    <div style={{width: '100%', justifyContent: 'center', display: 'flex', marginLeft: '-4px'}}>
      <DefaultRowSelectCheckboxRenderer selected={selected} changeHandler={change} />
    </div>
  );
}
